import React, { useState, useEffect, useRef } from 'react';
import { ASSET_IMAGES } from '../../assets/images';
import { SafeImage } from '../SafeImage';
import {
  Upload,
  Image as ImageIcon,
  FolderOpen,
  Link as LinkIcon,
  Check,
  X,
  Trash2,
  Search,
  Sparkles,
  AlertCircle,
  FileCheck,
  RefreshCw,
  Plus
} from 'lucide-react';

export interface MediaItem {
  id: string;
  name: string;
  url: string;
  category?: string;
  size?: string;
  uploadedAt: string;
}

// Built-in presets for equipment and blogs
export const PRESET_IMAGES = [
  {
    id: 'preset-water-ro',
    name: 'Reverse Osmosis Water Purification Plant',
    url: ASSET_IMAGES.heroWater,
    category: 'equipment',
    badge: 'Water Purification'
  },
  {
    id: 'preset-milk-atm',
    name: 'Commercial Digital Milk ATM Dispenser',
    url: ASSET_IMAGES.milkAtm,
    category: 'equipment',
    badge: 'Milk ATM'
  },
  {
    id: 'preset-water-station',
    name: 'Multi-Tap Commercial Water Vending Station',
    url: ASSET_IMAGES.waterStation,
    category: 'equipment',
    badge: 'Water Vending'
  },
  {
    id: 'preset-pasteurizer',
    name: 'Stainless Steel 304 Milk Pasteurizer Tank',
    url: ASSET_IMAGES.milkPasteurizer,
    category: 'equipment',
    badge: 'Pasteurizers'
  },
  {
    id: 'preset-cooking-oil',
    name: 'Automated Salad Cooking Oil ATM Dispenser',
    url: ASSET_IMAGES.cookingOil,
    category: 'equipment',
    badge: 'Cooking Oil'
  },
  {
    id: 'preset-blog-markets',
    name: 'Kenyan Commercial Markets & Kadogo Economy',
    url: ASSET_IMAGES.blogKenyanMarkets,
    category: 'blog',
    badge: 'Field Story'
  },
  {
    id: 'preset-blog-dairy',
    name: 'Dairy Farming & Cold-Chain Processing',
    url: ASSET_IMAGES.blogDairyFarming,
    category: 'blog',
    badge: 'Dairy Article'
  },
  {
    id: 'preset-blog-water',
    name: 'Clean Water Science & Borehole Membrane Filtration',
    url: ASSET_IMAGES.blogCleanWater,
    category: 'blog',
    badge: 'Water Article'
  },
  {
    id: 'preset-blog-milk-tap',
    name: 'Fresh Pasteurized Milk on Tap Kiosks',
    url: ASSET_IMAGES.blogFreshMilkOnTap,
    category: 'blog',
    badge: 'Retail Vending'
  }
];

/**
 * Utility: Compresses and resizes high-resolution camera images using client Canvas
 * to reduce transfer size and keep uploaded image records manageable.
 */
async function compressImageFile(file: File, maxWidth = 1200, maxHeight = 900, quality = 0.82): Promise<{ dataUrl: string; sizeFormatted: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Invalid image file'));
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Calculate aspect-ratio bounds
        if (width > maxWidth || height > maxHeight) {
          if (width / height > maxWidth / maxHeight) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Image processing is unavailable in this browser; the image was not uploaded.'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

        // Calculate approximate size in KB
        const head = 'data:image/jpeg;base64,';
        const rawSize = Math.round(((compressedDataUrl.length - head.length) * 3) / 4);
        const sizeFormatted = `${Math.round(rawSize / 1024)} KB`;

        resolve({ dataUrl: compressedDataUrl, sizeFormatted });
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

interface ImagePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentImage: string;
  onSelectImage: (imageUrl: string) => void;
  title?: string;
  categoryHint?: string;
}

export const ImagePickerModal: React.FC<ImagePickerModalProps> = ({
  isOpen,
  onClose,
  currentImage,
  onSelectImage,
  title = 'Select or Upload Image',
  categoryHint = 'equipment'
}) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'library' | 'presets' | 'url'>('upload');
  const [selectedUrl, setSelectedUrl] = useState<string>(currentImage);

  // Upload state
  const [dragOver, setDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [pendingFileName, setPendingFileName] = useState('');
  const [pendingSizeFormatted, setPendingSizeFormatted] = useState('');
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Custom URL input
  const [customUrlInput, setCustomUrlInput] = useState('');
  const [urlPreviewValid, setUrlPreviewValid] = useState<boolean | null>(null);

  // Filter in library / presets
  const [searchTerm, setSearchTerm] = useState('');
  const [presetCategoryFilter, setPresetCategoryFilter] = useState<'all' | 'equipment' | 'blog'>('all');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Media library state - loaded directly from database API
  const [mediaLibrary, setMediaLibrary] = useState<MediaItem[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);

  // Sync with database API directly (no local storage)
  useEffect(() => {
    if (!isOpen) return;
    let isMounted = true;
    const fetchRemoteMedia = async () => {
      setIsLoadingMedia(true);
      try {
        const res = await fetch('/api/media');
        const data = await res.json();
        if (!res.ok || !data.success || !Array.isArray(data.media)) {
          throw new Error(data.error || 'Could not load uploaded images from the database.');
        }
        if (isMounted) setMediaLibrary(data.media);
      } catch (err) {
        if (isMounted) {
          setUploadError(err instanceof Error ? err.message : 'Could not load uploaded images from the database.');
        }
      } finally {
        if (isMounted) setIsLoadingMedia(false);
      }
    };
    fetchRemoteMedia();
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedUrl(currentImage);
      setUploadError(null);
      setPendingFile(null);
      setPendingPreview(null);
    }
  }, [isOpen, currentImage]);

  if (!isOpen) return null;

  // Process file selection
  const handleFileChange = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select a valid image file (PNG, JPG, WEBP, or SVG).');
      return;
    }
    setUploadError(null);
    setIsProcessingUpload(true);
    setPendingFile(file);
    setPendingFileName(file.name);

    try {
      const { dataUrl, sizeFormatted } = await compressImageFile(file);
      setPendingPreview(dataUrl);
      setPendingSizeFormatted(sizeFormatted);
    } catch (err: any) {
      setUploadError(err.message || 'Error processing selected image.');
    } finally {
      setIsProcessingUpload(false);
    }
  };

  // Confirm pending upload and add to database media library
  const handleConfirmUpload = async () => {
    if (!pendingPreview) return;
    setIsProcessingUpload(true);
    try {
      const res = await fetch('/api/media/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: pendingFileName || 'Uploaded Photo.jpg',
          dataUrl: pendingPreview,
          category: categoryHint,
          size: pendingSizeFormatted || 'Optimized Image'
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success || !data.media?.id) {
        throw new Error(data.error || 'The image upload was not saved to the database.');
      }

      setMediaLibrary((prev) => [data.media, ...prev]);
      onSelectImage(`/api/media/${encodeURIComponent(data.media.id)}/image`);
      onClose();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'The image upload failed.');
    } finally {
      setIsProcessingUpload(false);
    }
  };

  // Delete an item from media library
  const handleDeleteMedia = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await fetch(`/api/media/${encodeURIComponent(id)}`, { method: 'DELETE' });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Could not delete this image from the database.');
      }
      setMediaLibrary((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Could not delete this image from the database.');
    }
  };

  const getPersistentImageUrl = (url: string): string => {
    const savedMedia = mediaLibrary.find((item) => item.url === url && item.url.startsWith('data:image/'));
    return savedMedia ? `/api/media/${encodeURIComponent(savedMedia.id)}/image` : url;
  };

  // Handle custom URL apply
  const handleApplyCustomUrl = () => {
    if (!customUrlInput.trim()) return;
    onSelectImage(customUrlInput.trim());
    onClose();
  };

  // Filtered lists
  const filteredLibrary = mediaLibrary.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPresets = PRESET_IMAGES.filter((preset) => {
    const matchCategory =
      presetCategoryFilter === 'all' || preset.category === presetCategoryFilter;
    const matchSearch =
      preset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      preset.badge.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-black/80 backdrop-blur-xs overflow-y-auto animate-fadeIn">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-3xl flex flex-col max-h-[90vh] shadow-2xl text-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/70">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-sky-600/20 text-sky-400 border border-sky-500/30 flex items-center justify-center">
              <ImageIcon className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white tracking-tight">{title}</h3>
              <p className="text-[11px] text-slate-400">
                Upload new machine photos or select from previously uploaded media and presets.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="px-5 border-b border-slate-800 flex items-center gap-2 bg-slate-950/40">
          <button
            type="button"
            onClick={() => setActiveTab('upload')}
            className={`flex items-center gap-2 px-3.5 py-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
              activeTab === 'upload'
                ? 'border-sky-500 text-sky-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Upload className="w-3.5 h-3.5" />
            <span>Upload New Image</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('library')}
            className={`flex items-center gap-2 px-3.5 py-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
              activeTab === 'library'
                ? 'border-sky-500 text-sky-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <FolderOpen className="w-3.5 h-3.5" />
            <span>Uploaded Images</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800 text-sky-400 font-bold tabular-nums">
              {mediaLibrary.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('presets')}
            className={`flex items-center gap-2 px-3.5 py-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
              activeTab === 'presets'
                ? 'border-sky-500 text-sky-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Preset Stock Images</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('url')}
            className={`flex items-center gap-2 px-3.5 py-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
              activeTab === 'url'
                ? 'border-sky-500 text-sky-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <LinkIcon className="w-3.5 h-3.5" />
            <span>External URL</span>
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* TAB 1: UPLOAD NEW IMAGE */}
          {activeTab === 'upload' && (
            <div className="space-y-4">
              {!pendingPreview ? (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleFileChange(file);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-8 sm:p-12 text-center cursor-pointer transition-all ${
                    dragOver
                      ? 'border-sky-500 bg-sky-950/30 scale-[0.99]'
                      : 'border-slate-800 bg-slate-950/60 hover:border-slate-700 hover:bg-slate-950'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileChange(file);
                    }}
                  />

                  <div className="w-14 h-14 rounded-2xl bg-sky-600/10 text-sky-400 border border-sky-500/20 flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-7 h-7" />
                  </div>

                  <h4 className="text-sm font-bold text-white mb-1">
                    Drop your machine or article image here
                  </h4>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto mb-4">
                    Click to browse files or drag and drop. Supports high-res JPG, PNG, WEBP, and camera photos.
                  </p>

                  <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold rounded-xl shadow-sm transition-colors">
                    <Plus className="w-3.5 h-3.5" />
                    <span>Browse Image Files</span>
                  </div>

                  <div className="mt-4 text-[11px] text-slate-500">
                    Auto-optimized & resized for fast loading across Kenyan mobile networks.
                  </div>
                </div>
              ) : (
                /* Preview and confirm step */
                <div className="bg-slate-950 rounded-2xl border border-slate-800 p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-sky-400 flex items-center gap-1.5">
                      <FileCheck className="w-4 h-4 text-emerald-400" />
                      <span>Ready to Upload & Apply</span>
                    </span>

                    <button
                      type="button"
                      onClick={() => {
                        setPendingFile(null);
                        setPendingPreview(null);
                      }}
                      className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3" />
                      <span>Choose Different Photo</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-5 items-center">
                    <div className="sm:col-span-5 aspect-video sm:aspect-square rounded-xl overflow-hidden bg-slate-900 border border-slate-800 flex items-center justify-center">
                      <img
                        src={pendingPreview}
                        alt="Upload preview"
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="sm:col-span-7 space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1">
                          Image Title or Label
                        </label>
                        <input
                          type="text"
                          value={pendingFileName}
                          onChange={(e) => setPendingFileName(e.target.value)}
                          placeholder="e.g. 500L Milk ATM Kiosk Roysambu"
                          className="w-full text-xs px-3.5 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-white focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                        />
                      </div>

                      <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800 text-xs space-y-1">
                        <div className="flex justify-between text-slate-400">
                          <span>Original File:</span>
                          <span className="text-slate-200 font-mono truncate max-w-[180px]">
                            {pendingFile?.name}
                          </span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                          <span>Optimized Size:</span>
                          <span className="text-emerald-400 font-semibold">{pendingSizeFormatted}</span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                          <span>Storage:</span>
                          <span className="text-slate-200">Shared database media library</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-1">
                        <button
                          type="button"
                          disabled={isProcessingUpload}
                          onClick={handleConfirmUpload}
                          className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 cursor-pointer"
                        >
                          <Check className="w-4 h-4" />
                          <span>
                            {isProcessingUpload ? 'Optimizing...' : 'Upload & Use This Image'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {uploadError && (
                <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-800/60 text-xs text-rose-300 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{uploadError}</span>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: UPLOADED IMAGES LIBRARY */}
          {activeTab === 'library' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="relative flex-1">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search your uploaded images..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full text-xs pl-9 pr-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab('upload')}
                  className="px-3 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shrink-0"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Upload More</span>
                </button>
              </div>

              {filteredLibrary.length === 0 ? (
                <div className="text-center py-12 border border-slate-800 rounded-2xl bg-slate-950/40 p-6 space-y-3">
                  <FolderOpen className="w-10 h-10 text-slate-600 mx-auto" />
                  <div className="text-sm font-semibold text-slate-300">
                    {isLoadingMedia
                      ? 'Loading images from the database…'
                      : searchTerm
                      ? 'No uploaded images match your search'
                      : 'No uploaded images yet'}
                  </div>
                  <p className="text-xs text-slate-500 max-w-sm mx-auto">
                    Upload your first machinery photo or article header image to build your permanent equipment gallery.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab('upload')}
                    className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold rounded-xl"
                  >
                    Upload Image Now
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {filteredLibrary.map((item) => {
                    const isSelected = selectedUrl === item.url;
                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelectedUrl(item.url)}
                        className={`group relative rounded-xl border p-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-sky-500 bg-sky-950/40 ring-2 ring-sky-500/30'
                            : 'border-slate-800 bg-slate-950 hover:border-slate-700'
                        }`}
                      >
                        <div className="aspect-video rounded-lg overflow-hidden bg-slate-900 mb-2 relative">
                          <img
                            src={item.url}
                            alt={item.name}
                            className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
                          />
                          {isSelected && (
                            <div className="absolute top-1.5 left-1.5 bg-sky-600 text-white p-1 rounded-md shadow-xs">
                              <Check className="w-3.5 h-3.5" />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={(e) => handleDeleteMedia(item.id, e)}
                            className="absolute top-1.5 right-1.5 bg-black/70 hover:bg-rose-600 text-slate-300 hover:text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete from library"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="text-[11px] font-semibold text-slate-200 truncate">
                          {item.name}
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-slate-500 mt-0.5">
                          <span>{item.size || 'Image'}</span>
                          <span>{item.uploadedAt}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: PRESET FACTORY & BLOG IMAGES */}
          {activeTab === 'presets' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                <div className="flex items-center gap-1.5 w-full sm:w-auto">
                  {[
                    { id: 'all', label: 'All Stock Presets' },
                    { id: 'equipment', label: 'Commercial Machines' },
                    { id: 'blog', label: 'Field & Blog Photos' }
                  ].map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setPresetCategoryFilter(f.id as any)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                        presetCategoryFilter === f.id
                          ? 'bg-sky-600 text-white'
                          : 'bg-slate-950 text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="relative w-full sm:w-60">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search presets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full text-xs pl-9 pr-3 py-1.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredPresets.map((preset) => {
                  const isSelected = selectedUrl === preset.url;
                  return (
                    <div
                      key={preset.id}
                      onClick={() => setSelectedUrl(preset.url)}
                      className={`group relative rounded-xl border p-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-sky-500 bg-sky-950/40 ring-2 ring-sky-500/30'
                          : 'border-slate-800 bg-slate-950 hover:border-slate-700'
                      }`}
                    >
                      <div className="aspect-video rounded-lg overflow-hidden bg-slate-900 mb-2 relative">
                        <SafeImage
                          src={preset.url}
                          fallbackCategory="equipment"
                          alt={preset.name}
                          className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
                        />
                        {isSelected && (
                          <div className="absolute top-1.5 left-1.5 bg-sky-600 text-white p-1 rounded-md shadow-xs">
                            <Check className="w-3.5 h-3.5" />
                          </div>
                        )}
                        <span className="absolute bottom-1.5 left-1.5 bg-slate-900/90 text-sky-400 text-[10px] font-bold px-2 py-0.5 rounded">
                          {preset.badge}
                        </span>
                      </div>
                      <div className="text-[11px] font-semibold text-slate-200 truncate">
                        {preset.name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 4: EXTERNAL URL */}
          {activeTab === 'url' && (
            <div className="space-y-4 max-w-lg mx-auto py-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  Direct Image URL (HTTPS)
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={customUrlInput}
                    onChange={(e) => {
                      setCustomUrlInput(e.target.value);
                      setUrlPreviewValid(null);
                    }}
                    placeholder="https://images.unsplash.com/... or https://..."
                    className="flex-1 text-xs px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (customUrlInput.trim()) {
                        setSelectedUrl(customUrlInput.trim());
                        setUrlPreviewValid(true);
                      }
                    }}
                    className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-semibold"
                  >
                    Verify
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Enter a direct web image link ending in .jpg, .png, or .webp.
                </p>
              </div>

              {customUrlInput && (
                <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-2">
                  <div className="text-xs font-semibold text-slate-300">Live Image Test:</div>
                  <div className="aspect-video rounded-lg overflow-hidden bg-slate-900 border border-slate-800">
                    <img
                      src={customUrlInput}
                      alt="URL preview"
                      onLoad={() => setUrlPreviewValid(true)}
                      onError={() => setUrlPreviewValid(false)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {urlPreviewValid === false && (
                    <div className="text-[11px] text-rose-400">
                      Could not load image from this URL. Please verify the link.
                    </div>
                  )}
                  {urlPreviewValid === true && (
                    <div className="text-[11px] text-emerald-400 flex items-center gap-1">
                      <Check className="w-3.5 h-3.5" />
                      <span>Valid image loaded successfully.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer with Selection Summary and Actions */}
        <div className="px-5 py-3.5 border-t border-slate-800 bg-slate-950/80 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <div className="w-9 h-9 rounded-lg overflow-hidden bg-slate-900 border border-slate-800 shrink-0">
              <SafeImage
                src={selectedUrl || currentImage}
                fallbackCategory="equipment"
                alt="Selected preview"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase font-bold text-slate-400">Active Selection</div>
              <div className="text-xs font-semibold text-slate-200 truncate max-w-xs">
                {selectedUrl.startsWith('data:') ? 'Custom Uploaded Data File' : selectedUrl}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                onSelectImage(getPersistentImageUrl(selectedUrl));
                onClose();
              }}
              className="px-5 py-2.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-sm shadow-sky-600/30 cursor-pointer"
            >
              <Check className="w-4 h-4" />
              <span>Confirm & Apply Image</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
